import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import { IconDefinition, faBirthdayCake, faClock } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { CreditIcon } from "@outschool/ui-components-shared";
import {
  getClassTypeIcon,
  useActivityString,
  usePriceDetailsString,
  useSelfPacedIconInfo
} from "@outschool/ui-components-website";
import React from "react";

export type SearchActivityType = Pick<
  Activity,
  | "uid"
  | "user_uid"
  | "isClub"
  | "is_ongoing_weekly"
  | "size_min"
  | "size_max"
  | "hasTeacherSchedule"
  | "duration_weeks"
  | "weekly_meetings"
  | "duration_minutes"
  | "allows_recurring_payment"
  | "price_cents"
  | "age_min"
  | "age_max"
  | "subject"
  | "canEnroll"
  | "isFlexSchedule"
  | "isSelfPaced"
> &
  Partial<Pick<Activity, "clubSection" | "priceCreditsPerMeeting">>;

interface PriceDetailsProps {
  activity: SearchActivityType;
  /** Display pricing in credits instead of currency */
  showCredits?: boolean;
  showFundingBadge?: boolean;
}

function IconBullet({
  icon,
  text
}: {
  icon: IconDefinition;
  text: string | null;
}) {
  const fixLineHeight = text?.search(/Group|1\-on\-1/) !== -1;
  return (
    <Box flex sx={{ textAlign: "left" }}>
      <Icon
        icon={icon}
        sx={{
          width: "1.25em",
          marginRight: "0.5em"
        }}
      />
      <Typography
        sx={{
          lineHeight: fixLineHeight ? "1em" : "inherit"
        }}
        variant={"caption"}
      >
        {text}
      </Typography>
    </Box>
  );
}

// TODO: This is only used by desktop listings, move into the DesktopListing component
export default function ListingDetails({
  activity,
  showCredits,
  showFundingBadge = false
}: PriceDetailsProps) {
  const { ageRangeString } = useActivityString();
  const {
    perClassString,
    classTypeString,
    perMeetingPriceString,
    detailedScheduleString
  } = usePriceDetailsString();
  const classTypeIcon = getClassTypeIcon(activity);
  const selfPacedIconInfo = useSelfPacedIconInfo(activity);
  const theme = useTheme();
  const { t } = useTranslation("client\\components\\search\\ListingDetails");

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        width: "100%",
        paddingLeft: "24px",
        paddingRight: "16px",
        paddingY: "16px"
      }}
    >
      <Box flex sx={{ paddingBottom: "4px" }}>
        <Box
          flex
          sx={{
            borderBottom: "1px solid #cccccc",
            width: "100%",
            gap: "0.5em",
            paddingRight: "1rem"
          }}
        >
          <Typography variant="display3" emphasized color={"grey.900"}>
            {showCredits ? (
              <Box sx={{ display: "flex" }}>
                <Box sx={{ paddingTop: "0.25rem", paddingRight: "0.5rem" }}>
                  <CreditIcon
                    fillColor={theme.palette.warning?.main!}
                    strokeColor={theme.palette.warning?.[700]!}
                    sx={{ weidth: "20px", height: "20px" }}
                  />
                </Box>
                {activity.priceCreditsPerMeeting}
              </Box>
            ) : (
              perMeetingPriceString(activity)
            )}
          </Typography>
          <Typography variant="caption" sx={{ alignSelf: "center" }}>
            {showCredits ? "credits " : ""}
            {perClassString(activity)}
          </Typography>
        </Box>
      </Box>
      {showFundingBadge && (
        <Box
          flex
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "grey.100",
            marginTop: "0.5em",
            borderRadius: "4px",
            padding: "0.5em",
            border: "1px solid",
            borderColor: "grey.300"
          }}
        >
          <Typography variant="body2" sx={{ color: "grey.700" }}>
            {t("State funding accepted.")}
          </Typography>
        </Box>
      )}
      <Box
        flex
        sx={{
          flexDirection: "column",
          height: "100%",
          justifyContent: "center",
          gap: "4px"
        }}
      >
        <IconBullet icon={classTypeIcon} text={classTypeString(activity)} />
        <IconBullet icon={faBirthdayCake} text={ageRangeString(activity)} />
        {activity.isSelfPaced ? (
          <IconBullet
            icon={selfPacedIconInfo.weeksOfFeedback.icon}
            text={selfPacedIconInfo.weeksOfFeedback.text}
          />
        ) : (
          <IconBullet icon={faClock} text={detailedScheduleString(activity)} />
        )}
      </Box>
    </Box>
  );
}
